// PR-P1 — edit a driver's PAY MODEL, never the final number. You pick the model
// and set its inputs; the salary computes. Every save asks: this month only, or
// all. One click each, no approval step.

import { useEffect, useState } from 'react';
import { Loader2, UserCog, Calculator, CalendarDays, Infinity as InfinityIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { apiClient, extractErrorMessage } from '@/lib/api';

type PayModel = 'fixed' | 'base_plus_per_trip' | 'base_plus_per_km' | 'target_with_bonus';

const MODEL_LABEL: Record<PayModel, string> = {
  fixed: 'Fixed monthly',
  base_plus_per_trip: 'Base + per trip',
  base_plus_per_km: 'Base + per km',
  target_with_bonus: 'Target + bonus per trip',
};
const MODEL_HELP: Record<PayModel, string> = {
  fixed: 'Same salary every month, regardless of trips.',
  base_plus_per_trip: 'Base plus an amount per completed trip.',
  base_plus_per_km: 'Base plus a rate for every km driven (from the tracker).',
  target_with_bonus: 'Base, plus a per-trip bonus once a monthly trip target is hit.',
};

interface Cfg {
  payModel: PayModel; baseSalarySar: number;
  perTripRateSar: number | null; perKmRateSar: number | null;
  targetTrips: number | null; bonusPerTripSar: number | null;
  source: 'config' | 'driver_default';
}

const monthLabel = (m: string) => {
  const [y, mo] = m.split('-').map(Number);
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

export function DriverPayEditorSheet({
  driverId, driverName, month, onClose, onSaved,
}: {
  driverId: string; driverName: string | null; month: string; onClose: () => void; onSaved: () => void;
}) {
  const [models, setModels] = useState<PayModel[]>(['fixed', 'base_plus_per_trip', 'base_plus_per_km', 'target_with_bonus']);
  const [payModel, setPayModel] = useState<PayModel>('fixed');
  const [base, setBase] = useState('');
  const [perTrip, setPerTrip] = useState('');
  const [perKm, setPerKm] = useState('');
  const [target, setTarget] = useState('');
  const [bonus, setBonus] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<null | 'this_month' | 'all'>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.get<{ models: PayModel[]; config: Cfg }>(`/api/v1/driver-finance/neom-pay/${driverId}/config`, { params: { month } })
      .then((r) => {
        if (cancelled) return;
        setModels(r.data.models);
        const c = r.data.config;
        setPayModel(c.payModel);
        setBase(c.baseSalarySar ? String(c.baseSalarySar) : '');
        setPerTrip(c.perTripRateSar != null ? String(c.perTripRateSar) : '');
        setPerKm(c.perKmRateSar != null ? String(c.perKmRateSar) : '');
        setTarget(c.targetTrips != null ? String(c.targetTrips) : '');
        setBonus(c.bonusPerTripSar != null ? String(c.bonusPerTripSar) : '');
      })
      .catch((e) => { if (!cancelled) setError(extractErrorMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [driverId, month]);

  async function save(scope: 'this_month' | 'all') {
    setBusy(scope); setError(null);
    try {
      await apiClient.put(`/api/v1/driver-finance/neom-pay/${driverId}/config`, {
        payModel,
        baseSalarySar: Number(base) || 0,
        perTripRateSar: payModel === 'base_plus_per_trip' && perTrip !== '' ? Number(perTrip) : null,
        perKmRateSar: payModel === 'base_plus_per_km' && perKm !== '' ? Number(perKm) : null,
        targetTrips: payModel === 'target_with_bonus' && target !== '' ? Number(target) : null,
        bonusPerTripSar: payModel === 'target_with_bonus' && bonus !== '' ? Number(bonus) : null,
        scope,
        month,
      });
      onSaved();
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally { setBusy(null); }
  }

  return (
    <Sheet open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><UserCog className="h-5 w-5" /> Pay model — {driverName ?? 'driver'}</SheetTitle>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 space-y-4" data-pay-editor>
            <div className="rounded-md bg-muted/40 border border-border p-3 text-xs text-muted-foreground flex items-start gap-2">
              <Calculator className="h-4 w-4 mt-0.5 shrink-0" />
              <span>You pick the formula and its inputs; the salary computes from trips and the tracker. You never type the final salary.</span>
            </div>

            <div className="space-y-1.5">
              <Label>Pay model</Label>
              <div className="grid gap-2">
                {models.map((m) => (
                  <button key={m} type="button" onClick={() => setPayModel(m)}
                    className={'rounded-lg border p-3 text-start transition-colors ' +
                      (payModel === m ? 'border-primary bg-primary/10' : 'border-border hover:bg-secondary')}>
                    <div className="text-sm font-medium">{MODEL_LABEL[m]}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{MODEL_HELP[m]}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pe-base">Base salary (SAR / month)</Label>
              <Input id="pe-base" type="number" min="0" step="0.01" value={base} onChange={(e) => setBase(e.target.value)} placeholder="0" />
              <p className="text-[11px] text-muted-foreground">Prorated automatically for partial date ranges.</p>
            </div>

            {payModel === 'base_plus_per_trip' && (
              <div className="space-y-1.5">
                <Label htmlFor="pe-pertrip">Per-trip rate (SAR)</Label>
                <Input id="pe-pertrip" type="number" min="0" step="0.01" value={perTrip} onChange={(e) => setPerTrip(e.target.value)} placeholder="(blank = use contract rate)" />
                <p className="text-[11px] text-muted-foreground">Leave blank to use the per-trip driver rate from the contract (trip-math).</p>
              </div>
            )}
            {payModel === 'base_plus_per_km' && (
              <div className="space-y-1.5">
                <Label htmlFor="pe-perkm">Per-km rate (SAR)</Label>
                <Input id="pe-perkm" type="number" min="0" step="0.01" value={perKm} onChange={(e) => setPerKm(e.target.value)} placeholder="0" />
                <p className="text-[11px] text-muted-foreground">Applied to tracker km for the trucks this driver drove.</p>
              </div>
            )}
            {payModel === 'target_with_bonus' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="pe-target">Trip target</Label>
                  <Input id="pe-target" type="number" min="0" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="0" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pe-bonus">Bonus / trip (SAR)</Label>
                  <Input id="pe-bonus" type="number" min="0" step="0.01" value={bonus} onChange={(e) => setBonus(e.target.value)} placeholder="0" />
                </div>
              </div>
            )}

            {error && <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{error}</div>}
          </div>
        )}

        <div className="shrink-0 border-t border-border bg-muted/30 p-4 space-y-2">
          <p className="text-xs text-muted-foreground text-center">Apply this change to:</p>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" disabled={!!busy || loading} onClick={() => save('this_month')} className="gap-1.5">
              {busy === 'this_month' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarDays className="h-4 w-4" />} {monthLabel(month)} only
            </Button>
            <Button disabled={!!busy || loading} onClick={() => save('all')} className="gap-1.5">
              {busy === 'all' ? <Loader2 className="h-4 w-4 animate-spin" /> : <InfinityIcon className="h-4 w-4" />} All months
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
